import React, { ReactElement, useEffect, useState } from 'react'
import { Offer as OfferType, Venue as VenueType, VenueProps } from './types'
import ArrowRight from '~/Arrows/ArrowRight.svg'
import Nav from '@/components/Nav'
import { useRouter } from 'next/router'
import PaymentMethods from './PaymentMethods'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
  CardAuthRequestTypes,
  PaymentData,
  PaymentDataAtom,
  paymentsMethodModalAtom,
  cardAuthRequestTypeAtom,
} from '@/state/payment'
import {
  statusAtom,
  userInfoAtom,
  venueAtom,
  verifyComponentAtom,
  payWithApplePayAtom,
  payWithGooglePayAtom,
  tryOpeningHostedPayentPageAtom,
  orderingStatusAtom,
  applePayUserAtom,
  verifyStepAtom,
  suggestAppAtom,
  tabUuidAtom,
  venueCustomerAtom,
  venueStatusClaimAtom,
  venueStatusSignupBonusPointsAtom,
  clientVerificationRequiredAtom,
} from '@/state/global'
import { customerCheckin, getOffersAPI, getTabAmount, submitCustomerInfo, venueCustomersApi } from '@/utils/apis'
import Offers from '../offers'
import useMutationWithRedirect from '@/hooks/UseMutationWithRedirect'
import { useAuthQuery } from '@/hooks/useAuthQuery'
import RemoteImage from '@/components/RemoteImage'
import { menuModalAtom, nextRoundOrderAtom, venueTableAtom } from '@/state/order'
import dynamic from 'next/dynamic'
import { useApplePay } from '@/hooks/useApplePay'
import { useGooglePay } from '@/hooks/useGooglePay'
import { useMagensaGooglePay } from '@/hooks/useMagensaGooglePay'
import { useMagensaApplePay } from '@/hooks/useMagensaApplePay'
import { CustomerSubmit, VerifyStep } from '../verify/types'
import SuggestApp from './SuggestApp'
import { floatCurrency } from '@/utils/currency'
import { getVenueOpensStatus } from '@/utils/venueHours'
import { TOKEN_EXPIRE_AFTER_MS, PaymentMethodInputTypes } from '@/utils/constants'
import { User } from '@/utils/types'
import { NEXT_ROUND_ORDER_KEY } from '@/constants'
import NoVenueStatusBanner from '../venue-status/NoVenueStatusBanner'
import VenueStatus from '../venue-status'
import { VenueCustomerStatus } from '../venue-status/types'

const Verify = dynamic(() => import('@/components/verify/Verify'))
const Menu = dynamic(() => import('@/components/Menu'))
const Order = dynamic(() => import('@/components/order'))
const Offer = dynamic(() => import('@/components/offers/OfferModal'))

function Venue({ venue_uuid }: VenueProps) {
  const router = useRouter()
  const { s2p, ordering } = router.query

  // local
  const [tabAmount, setTabAmount] = useState(0)
  const [openingTabLoadingIndicator, setOpeningTabLoadingIndicator] = useState(false)
  const [userOpensTab, setUserOpensTab] = useState(false)
  const [venueCustomerStatus, setVenueCustomerStatus] = useState<VenueCustomerStatus | null>(null)
  const setVenueCustomer = useSetAtom(venueCustomerAtom)

  // global
  const [savedTabUuid, setSavedTabUuid] = useAtom(tabUuidAtom)
  const [tryOpeningHostedPayentPage, setTryOpeningHostedPaymentPage] = useAtom(tryOpeningHostedPayentPageAtom)
  const { handleApplePayClick, isApplePaySupported } = useApplePay()
  const { handleGooglePayClick, isGooglePaySupported } = useGooglePay()
  const { handleMagensaGooglePayClick, isMagensaGooglePaySupported } = useMagensaGooglePay()
  const { handleMagensaApplePayClick, isMagensaApplePaySupported, isMagensaApplePayInitialized } = useMagensaApplePay()
  const [payWithApplePay, setPayWithApplePay] = useAtom(payWithApplePayAtom)
  const [payWithGooglePay, setPayWithGooglePay] = useAtom(payWithGooglePayAtom)
  const applePayUser = useAtomValue(applePayUserAtom)
  const [isVerifyComponentOpen, openVerifyComponent] = useAtom(verifyComponentAtom)
  const verifyStatus = useAtomValue(statusAtom)
  const [user, setUserInfo] = useAtom(userInfoAtom)
  const selectedPaymentMethod = useAtomValue(PaymentDataAtom)
  const venue = useAtomValue(venueAtom)
  const openMenuComponent = useSetAtom(menuModalAtom)
  const [isPaymentModalOpen, openPaymentMethodsComponent] = useAtom(paymentsMethodModalAtom)
  const [verifyStep, setVerifyStep] = useAtom(verifyStepAtom)
  const [suggestApp, setSuggestApp] = useAtom(suggestAppAtom)
  const setCardAuthRequestTypeAtom = useSetAtom(cardAuthRequestTypeAtom)
  const selectedTable = useAtomValue(venueTableAtom)
  const setNextRoundOrder = useSetAtom(nextRoundOrderAtom)
  const [venuestatusclaim, setVenueStatusClaim] = useAtom(venueStatusClaimAtom)
  const [venueStatusSignupBonusPoints, setVenueStatusSignupBonusPoints] = useAtom(venueStatusSignupBonusPointsAtom)
  const clientVerificationRequired = useAtomValue(clientVerificationRequiredAtom)
  const { mutation: venueCustomersMutation } = useMutationWithRedirect({
    mutationFn: () => venueCustomersApi(venue_uuid as string, user?.token ?? ''),
  })

  const {
    data: offers,
    isLoading: offersIsLoading,
    refetch: refetchOffers,
  } = useAuthQuery<OfferType[]>(['offer', venue_uuid], () => getOffersAPI(venue_uuid as string, user))

  useEffect(() => {
    if (verifyStep === VerifyStep.Code) {
      openVerifyComponent(true)
    } else if (verifyStep === VerifyStep.Info) {
      setOpeningTabLoadingIndicator(false)
    }
  }, [verifyStep])

  useEffect(() => {
    // this checks that the next round order is not from a different venue, this is when a user opens an empty tab
    const nextRoundOrderFromStorage = JSON.parse(localStorage.getItem(NEXT_ROUND_ORDER_KEY) as string)
    if (nextRoundOrderFromStorage?.order?.length > 0) {
      const venueId = nextRoundOrderFromStorage.venue_id
      if (venueId && venueId !== venue.venue_uuid) {
        setNextRoundOrder({ order: [], venue_id: '' })
        localStorage.removeItem(NEXT_ROUND_ORDER_KEY)
      }
    }
  }, [])

  useEffect(() => {
    if (user) {
      processVenueCustomer(user)
    }
  }, [verifyStatus])

  useEffect(() => {
    if (venuestatusclaim) {
      setVenueStatusClaim(null)
    }
    if (venueStatusSignupBonusPoints) {
      setVenueStatusSignupBonusPoints(null)
    }
  }, [venuestatusclaim])

  const processVenueCustomer = async (user?: User | null) => {
    try {
      if (user?.token === null) return
      if (venue.venue_status_enabled) {
        const vc = await venueCustomersMutation.mutateAsync(null)
        setVenueCustomer(vc)
        const venueStatus = vc.venues?.find((v: VenueCustomerStatus) => v.venue_uuid === venue.venue_uuid)
        if (venueStatus) {
          setVenueCustomerStatus(venueStatus)
        }
      }
    } catch (error) {
      console.error('Error processing venue customer', error)
    }
  }

  const { mutation: registerCustomer, error: registerCustomerError } = useMutationWithRedirect({
    mutationFn: submitCustomerInfo,
    onSuccess: (data) => {
      if (data?.title === 'Invalid Data') {
        return openVerifyComponent(true)
      } else {
        setUserInfo(data)
        localStorage.setItem('user', JSON.stringify(data))
        localStorage.setItem('verifiedTime', JSON.stringify(Date.now() + TOKEN_EXPIRE_AFTER_MS))
        if (data?.require_sms_verification) {
          setVerifyStep(VerifyStep.Code)
        } else {
          handleOpenTab()
        }
      }
    },
    onError: () => {},
  })

  const { mutation: openTab, error } = useMutationWithRedirect({
    mutationFn: (data: any) => customerCheckin(data, user ?? JSON.parse(localStorage.getItem('user') as string)),
    onSuccess: async (data) => {
      if (data?.tab_uuid) {
        setSavedTabUuid(data?.tab_uuid)
        setUserOpensTab(false)
        await router.push(
          `/tab/?venue_uuid=${data.venue_uuid}&tab_uuid=${data.tab_uuid}${ordering === 'false' ? '&ordering=false' : ''}`
        )
      }
    },
    onError: () => {
      setOpeningTabLoadingIndicator(false)
    },
  })

  const { mutation: getTabAmountMutation } = useMutationWithRedirect({
    mutationFn: () => getTabAmount(savedTabUuid as string, venue_uuid, user?.token ?? ''),
  })

  const handleGetTabAmount = async () => {
    const amount = await getTabAmountMutation.mutateAsync(null)
    setTabAmount(amount)
  }

  useEffect(() => {
    if (savedTabUuid) {
      handleGetTabAmount()
    }
  }, [savedTabUuid])

  useEffect(() => {
    refetchOffers()
  }, [user, verifyStatus])

  useEffect(() => {
    if (userOpensTab && !isVerifyComponentOpen) {
      handleOpenTab()
    } else if (tryOpeningHostedPayentPage && !isVerifyComponentOpen) {
      openPaymentMethodsComponent(true)
    } else {
      setOpeningTabLoadingIndicator(false)
    }
  }, [isVerifyComponentOpen])

  if (!venue)
    return (
      <div className="bg-black min-h-screen flex items-center justify-center ">
        <span className="text-gray-600 text-4xl">Error Loading Venue...</span>
      </div>
    )

  const handleOpenTab = async (payment?: PaymentData) => {
    setUserOpensTab(true)
    setCardAuthRequestTypeAtom(CardAuthRequestTypes.CHECKIN)

    const localStorageUser = JSON.parse(localStorage.getItem('user') as string)
    let validStoredUser: null | User =
      localStorageUser && typeof localStorageUser === 'object' && localStorageUser?.phone
        ? localStorageUser
        : user
        ? user
        : null

    let newApplePayPayment = null
    let newGooglePayPayment = null
    if (payWithApplePay && isApplePaySupported && selectedPaymentMethod?.input_mode !== 'fortis_apple_pay_web') {
      newApplePayPayment = await handleApplePayClick()
    } else if (
      payWithApplePay &&
      isMagensaApplePaySupported &&
      selectedPaymentMethod?.input_mode !== PaymentMethodInputTypes.MAGENSA_UNIGATE_APPLE_PAY_WEB
    ) {
      try {
        newApplePayPayment = await handleMagensaApplePayClick()
      } catch (err) {
        console.error('[handleOpenTab] Magensa Apple Pay error:', err)
      }
    } else if (payWithGooglePay && isGooglePaySupported && selectedPaymentMethod?.input_mode !== 'fortis_google_pay_web') {
      newGooglePayPayment = await handleGooglePayClick()
    } else if (
      payWithGooglePay &&
      isMagensaGooglePaySupported &&
      selectedPaymentMethod?.input_mode !== PaymentMethodInputTypes.MAGENSA_UNIGATE_GOOGLE_PAY_WEB
    ) {
      newGooglePayPayment = await handleMagensaGooglePayClick()
    }

    if (
      !payWithApplePay &&
      !payWithGooglePay &&
      (!selectedPaymentMethod || selectedPaymentMethod.card_token === '') &&
      !payment
    ) {
      return openPaymentMethodsComponent(true)
    }

    if (
      !validStoredUser ||
      (verifyStatus === 'not_verified' && validStoredUser?.require_sms_verification && clientVerificationRequired)
    ) {
      if (!newApplePayPayment && !newGooglePayPayment) {
        return openVerifyComponent(true)
      }
    }

    let availablePayment = null

    availablePayment = selectedPaymentMethod?.card_token === '' ? payment : selectedPaymentMethod

    if ((newApplePayPayment as any)?.paymentMethod) {
      availablePayment = (newApplePayPayment as any)?.paymentMethod
    } else if ((newGooglePayPayment as any)?.paymentMethod) {
      availablePayment = (newGooglePayPayment as any)?.paymentMethod
    }
    setOpeningTabLoadingIndicator(true)

    const applePayUserData = (newApplePayPayment as any)?.paymentUser
    const googlePayUserData = (newGooglePayPayment as any)?.paymentUser

    const paymentUser = applePayUserData || googlePayUserData

    if (paymentUser && !validStoredUser) {
      const customerSubmit: CustomerSubmit = {
        first_name: paymentUser?.first_name || '',
        last_name: paymentUser?.last_name || '',
        email: paymentUser?.email || '',
        phone: paymentUser?.phone || '',
        skip_phone_verification: false,
      }
      const allFieldsPresent = !!(
        customerSubmit.first_name &&
        customerSubmit.last_name &&
        customerSubmit.email &&
        customerSubmit.phone
      )
      if (allFieldsPresent) {
        return await registerCustomer.mutateAsync(customerSubmit)
      }
      setVerifyStep(VerifyStep.Info)
      return openVerifyComponent(true)
    } else {
      if (
        !validStoredUser ||
        (verifyStatus === 'not_verified' && validStoredUser?.require_sms_verification && clientVerificationRequired)
      ) {
        if (validStoredUser) {
          setVerifyStep(VerifyStep.Code)
        }
        return openVerifyComponent(true)
      }
      const body = {
        payment_method: availablePayment,
        venue_uuid,
        ...(selectedTable ? { table_id: selectedTable.id } : {}),
      }
      return await openTab.mutateAsync(body)
    }
  }

  const VenueStatusDisplay = ({ venue }: { venue: VenueType }) => {
    try {
      const { isOpen, opensAt } = getVenueOpensStatus(venue)
      if (openingTabLoadingIndicator) {
        return (
          <div className="h-[75px] w-full py-7 text-center bg-primary-blue flex items-center justify-center">
            <svg
              width="45"
              height="50"
              viewBox="0 0 145 50"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="animate-spin"
            >
              <path
                d="M121.408 0C109.718 0 103.134 9.99898 99.194 13.9392C90.239 22.8943 88.1307 2.0699 80.5854 2.0699H72.3979H42.7182V24.1889C42.7182 29.7667 40.3797 32.9137 35.7026 32.9137C31.7444 32.9137 29.6771 30.4831 29.6771 26.3484V2.06478H0V30.3935C0 43.2556 7.82161 50 23.0273 50C34.7175 50 41.3008 40.001 45.241 36.0608C54.1961 27.1057 56.3044 47.9327 63.8497 47.9327H101.717V25.8111C101.717 20.2333 104.055 17.0863 108.732 17.0863C112.691 17.0863 114.758 19.5169 114.758 23.6516V47.9352H144.438V19.6065C144.43 6.74445 136.608 0 121.408 0Z"
                fill="white"
              />
            </svg>
          </div>
        )
      }
      // check is the venue is Open

      if (isOpen) {
        return (
          <button className="h-[75px] w-full py-7 text-center bg-primary-blue" onClick={() => handleOpenTab()}>
            <span>Open Tab</span>
          </button>
        )
      } else if (opensAt !== '') {
        return (
          <div className="h-[75px] w-full py-7 text-center bg-primary-red">
            <span>{opensAt}</span>
          </div>
        )
      } else {
        return (
          <div className="h-[75px] w-full py-7 text-center bg-primary-red">
            <span>Closed</span>
          </div>
        )
      }
    } catch (error) {
      return (
        <div className="w-full py-8 text-center bg-primary-red">
          <span>Closed</span>
        </div>
      )
    }
  }

  const createVenueStatus = (): ReactElement | null => {
    if ((venue?.venue_status_enabled ?? false) === false) return null

    return venueCustomerStatus &&
      venueCustomerStatus.venue_status_signup_on !== null &&
      venueCustomerStatus.venue_status_signup_on !== undefined ? (
      <VenueStatus animateProgressBar={false} />
    ) : (
      <NoVenueStatusBanner />
    )
  }

  return (
    <>
      <div className="bg-black relative min-h-screen overflow-y-auto overflow-x-hidden">
        {error}
        <div className="bg-white">
          <Nav pathname="Venue" />
          <div className="flex flex-col items-center">
            <div id="divider" className="h-[0.5px] w-full bg-gray-400" />
            <section id="upper-venue-section" className="layout flex flex-col items-center">
              <h1
                className={`text-black mt-4 font-lexa my-3 uppercase tracking-[-0.1rem] text-center w-full ${
                  venue.name.trim().length < 20 ? 'text-4xl' : venue.name.trim().length < 35 ? 'text-3xl' : 'text-2xl'
                }`}
              >
                {venue.name.trim()}
              </h1>

              <RemoteImage image_url={venue?.images[0]?.image_url} alt_name="venue logo" style_classes="rounded-md w-36" />

              <div id="divider" className="h-[0.5px] w-full bg-gray-400 mt-5" />
              <div className="flex justify-between items-center w-full py-2 text-sm">
                <button
                  className="px-3 py-1 rounded-xl flex items-center justify-end w-full"
                  onClick={() => openMenuComponent(true)}
                >
                  <span className="tracking-tight">VIEW MENU</span> <ArrowRight className="w-6 mb-0.5 ml-1" fill="#000" />
                </button>
              </div>
            </section>
          </div>
        </div>

        <div className="pb-[110px]">
          <Offers offers={offers} venue_uuid={venue_uuid} pointerEventsDisabled={isPaymentModalOpen} />
          {createVenueStatus()}
          <br />
        </div>
        <section id="open-tab-payment" className="bg-black fixed text-white pb-safe bottom-0 w-full mx-auto max-w-[820px]">
          {savedTabUuid && !openingTabLoadingIndicator ? (
            <>
              <div
                className="h-[75px] py-3 pl-5 bg-primary-blue"
                onClick={() =>
                  router.push(
                    `/tab/?venue_uuid=${venue_uuid}&tab_uuid=${savedTabUuid}${ordering === 'false' ? '&ordering=false' : ''}${
                      s2p === 'true' ? '&s2p=true' : ''
                    }`
                  )
                }
              >
                <h2 className="font-light">{venue.name}</h2>
                <span className="text-xl">Running Tab - {floatCurrency(tabAmount, 0)} </span>
              </div>
            </>
          ) : (
            <>
              <PaymentMethods />
              {VenueStatusDisplay({ venue })}
            </>
          )}
        </section>
      </div>

      {/* components */}
      <Verify cancel={() => setUserOpensTab(false)} />
      <Menu
        venueName={venue.name.trim()}
        venueId={venue_uuid}
        offers={offers}
        offersIsLoading={offersIsLoading}
        ordering={ordering === 'false' ? false : true}
      />
      <Order />
      <Offer />
      {suggestApp && <SuggestApp />}
    </>
  )
}

export default Venue
